import { useState, useCallback } from 'react';
import QRCode from 'qrcode';
import { apiClient } from '../api/client';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_CARD,
} from '../components/legacy-ball/table-management/designTokens';

interface GuestResult {
  id: string;
  fullName: string;
  ticketNumber: string | null;
  chapterAffiliation: string | null;
  seat?: Array<{ seatNumber: number; table: { tableNumber: number; tableName?: string; zone?: { name: string } } }>;
  registration?: { ticket?: { tierName: string; order?: { referenceId: string } } | null } | null;
  dietaryPreference?: string | null;
}

interface GuestsResponse {
  guests: GuestResult[];
  totalGuests: number;
}

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '800px', margin: '0 auto' },
  title: { color: PRIMARY, fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' },
  subtitle: { color: TEXT_MUTED, fontSize: '0.9rem', marginBottom: '1.5rem' },
  searchRow: { display: 'flex', gap: '8px', marginBottom: '1rem' },
  input: { flex: 1, padding: '0.6rem 0.85rem', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '6px', fontSize: '0.9rem' },
  searchBtn: { padding: '0.6rem 1.2rem', backgroundColor: PRIMARY, color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' },
  resultsList: { display: 'flex', flexDirection: 'column' as const, gap: '8px', marginBottom: '1.5rem' },
  resultCard: { padding: '12px 16px', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '8px', cursor: 'pointer', backgroundColor: BG_CARD, transition: 'border-color 0.15s' },
  resultCardSelected: { padding: '12px 16px', border: `2px solid ${PRIMARY}`, borderRadius: '8px', cursor: 'pointer', backgroundColor: '#f0fdf4' },
  previewSection: { marginTop: '1.5rem', padding: '1.5rem', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '12px', backgroundColor: BG_CARD, textAlign: 'center' as const },
  qrImage: { width: '200px', height: '200px', margin: '0 auto 12px' },
  guestNamePreview: { fontSize: '1.2rem', fontWeight: 700, color: TEXT_DARK, marginBottom: '4px' },
  ticketPreview: { fontSize: '0.9rem', color: PRIMARY, fontWeight: 600, marginBottom: '12px' },
  detailRow: { fontSize: '0.85rem', color: TEXT_MUTED, marginBottom: '4px' },
  printBtn: { marginTop: '1rem', padding: '12px 24px', backgroundColor: PRIMARY, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: 700, cursor: 'pointer' },
  bulkBtn: { marginTop: '0.5rem', padding: '10px 20px', backgroundColor: '#e0e0e0', color: '#333', border: 'none', borderRadius: '8px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' },
};

export function QrGeneratorPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<GuestResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState<GuestResult | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  const appBaseUrl = window.location.origin;

  const handleSearch = useCallback(async () => {
    if (!searchTerm.trim()) return;
    setSearching(true);
    setSelectedGuest(null);
    setQrDataUrl(null);
    try {
      const data = await apiClient<GuestsResponse>(`/table-management/guests?query=${encodeURIComponent(searchTerm.trim())}`);
      setResults(data.guests || []);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, [searchTerm]);

  const handleSelectGuest = async (guest: GuestResult) => {
    setSelectedGuest(guest);
    if (guest.ticketNumber) {
      const url = `${appBaseUrl}/guest-card/${guest.ticketNumber}`;
      const dataUrl = await QRCode.toDataURL(url, { width: 300, margin: 2, color: { dark: '#0f2d1f', light: '#ffffff' } });
      setQrDataUrl(dataUrl);
    } else {
      setQrDataUrl(null);
    }
  };

  const handlePrintPdf = () => {
    if (!selectedGuest || !qrDataUrl) return;
    const seat = selectedGuest.seat?.[0];
    const tier = selectedGuest.registration?.ticket?.tierName || '';
    const order = selectedGuest.registration?.ticket?.order?.referenceId || '';
    const table = seat ? (seat.table.tableName || `Table ${seat.table.tableNumber}`) : '';
    const seatNum = seat ? `Seat ${seat.seatNumber}` : '';
    const zone = seat?.table?.zone?.name || '';
    const meal = selectedGuest.dietaryPreference || '';
    const chapter = selectedGuest.chapterAffiliation || '';

    const html = `<!DOCTYPE html><html><head><title>Guest Card - ${selectedGuest.fullName}</title>
<style>
  @page { size: 4in 6in; margin: 0; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; margin: 0; padding: 20px; display: flex; flex-direction: column; align-items: center; min-height: 100vh; box-sizing: border-box; }
  .card { width: 100%; max-width: 360px; border: 2px solid #1a6b3c; border-radius: 16px; overflow: hidden; }
  .header { background: linear-gradient(135deg, #1a6b3c, #0f4a28); color: #fff; text-align: center; padding: 16px 12px; }
  .header img { width: 40px; height: 40px; border-radius: 8px; margin-bottom: 4px; }
  .header h2 { margin: 0; font-size: 14px; }
  .header p { margin: 2px 0 0; font-size: 11px; opacity: 0.85; }
  .body { padding: 16px; text-align: center; }
  .name { font-size: 20px; font-weight: 800; color: #0f2d1f; margin-bottom: 4px; }
  .ticket { display: inline-block; padding: 3px 12px; background: #f0fdf4; border: 1px solid #86efac; border-radius: 12px; font-size: 13px; font-weight: 600; color: #166534; margin-bottom: 12px; }
  .qr { margin: 12px auto; }
  .qr img { width: 160px; height: 160px; }
  .info { text-align: left; padding: 0 8px; margin-top: 12px; border-top: 1px solid #e5e7eb; padding-top: 12px; }
  .info-row { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; font-size: 12px; }
  .info-row .icon { font-size: 14px; flex-shrink: 0; }
  .info-row .label { color: #6b7280; font-size: 10px; text-transform: uppercase; font-weight: 600; }
  .info-row .value { color: #111; font-weight: 600; font-size: 13px; }
  .footer { text-align: center; padding: 10px; font-size: 10px; color: #999; border-top: 1px solid #e5e7eb; }
</style></head><body>
<div class="card">
  <div class="header">
    <img src="/KDULogo.PNG" alt="KDU" />
    <h2>KDU Georgia 2026</h2>
    <p>Legacy Ball</p>
  </div>
  <div class="body">
    <div class="name">${selectedGuest.fullName}</div>
    <div class="ticket">${selectedGuest.ticketNumber}${tier ? ` • ${tier}` : ''}</div>
    <div class="qr"><img src="${qrDataUrl}" alt="QR Code" /></div>
    <div class="info">
      ${table ? `<div class="info-row"><span class="icon">🪑</span><div><div class="label">Table / Seat</div><div class="value">${table}, ${seatNum}</div></div></div>` : ''}
      ${zone ? `<div class="info-row"><span class="icon">🏷️</span><div><div class="label">Zone</div><div class="value">${zone}</div></div></div>` : ''}
      ${meal ? `<div class="info-row"><span class="icon">🍽️</span><div><div class="label">Meal</div><div class="value">${meal}</div></div></div>` : ''}
      ${chapter ? `<div class="info-row"><span class="icon">📍</span><div><div class="label">Chapter</div><div class="value">${chapter}</div></div></div>` : ''}
      ${order ? `<div class="info-row"><span class="icon">📦</span><div><div class="label">Order</div><div class="value">${order}</div></div></div>` : ''}
    </div>
  </div>
  <div class="footer">Scan QR code for live info • KDU-HUB</div>
</div>
</body></html>`;

    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 400); }
  };

  const handlePrintVenueSignage = async () => {
    const url = `${appBaseUrl}/my-info`;
    const qr = await QRCode.toDataURL(url, { width: 400, margin: 3, color: { dark: '#0f2d1f', light: '#ffffff' } });
    const html = `<!DOCTYPE html><html><head><title>KDU - Find My Info</title>
<style>
  @page { size: letter; margin: 0; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; margin: 0; padding: 0; display: flex; align-items: center; justify-content: center; min-height: 100vh; background: #fff; }
  .poster { text-align: center; padding: 40px; max-width: 600px; }
  .logo { width: 80px; height: 80px; border-radius: 12px; margin-bottom: 16px; }
  h1 { font-size: 28px; color: #0f2d1f; margin: 0 0 8px; }
  h2 { font-size: 18px; color: #1a6b3c; margin: 0 0 24px; font-weight: 600; }
  .qr { margin: 0 auto 24px; }
  .qr img { width: 280px; height: 280px; border: 3px solid #1a6b3c; border-radius: 16px; padding: 12px; }
  .instructions { font-size: 20px; color: #333; font-weight: 600; margin-bottom: 8px; }
  .sub { font-size: 14px; color: #666; }
  .url { font-size: 12px; color: #999; margin-top: 16px; font-family: monospace; }
</style></head><body>
<div class="poster">
  <img class="logo" src="/KDULogo.PNG" alt="KDU" />
  <h1>KDU Georgia 2026</h1>
  <h2>Legacy Ball</h2>
  <div class="qr"><img src="${qr}" alt="QR Code" /></div>
  <p class="instructions">Scan to find your table & seat info</p>
  <p class="sub">Use your ticket number or order number to look up your details</p>
  <p class="url">${url}</p>
</div>
</body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 400); }
  };

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>🎫 QR Guest Card Generator</h1>
      <p style={styles.subtitle}>Search for a guest, then generate a printable QR card for their welcome packet.</p>

      {/* Venue Signage Button */}
      <div style={{ marginBottom: '1.5rem', padding: '1rem', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '10px', backgroundColor: '#f0fdf4', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontWeight: 600, color: TEXT_DARK, fontSize: '0.9rem' }}>📍 Venue Signage QR</div>
          <div style={{ fontSize: '0.8rem', color: TEXT_MUTED }}>Print a poster for guests to scan and look up their own info</div>
        </div>
        <button style={{ ...styles.searchBtn, whiteSpace: 'nowrap' }} onClick={handlePrintVenueSignage}>🖨️ Print Signage</button>
      </div>

      {/* Search */}
      <div style={styles.searchRow}>
        <input
          style={styles.input}
          type="text"
          placeholder="Search by name, ticket #, or order ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
          aria-label="Search guests"
        />
        <button style={styles.searchBtn} onClick={handleSearch} disabled={searching}>
          {searching ? '...' : '🔍 Search'}
        </button>
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div style={styles.resultsList}>
          {results.slice(0, 10).map((guest) => (
            <div
              key={guest.id}
              style={selectedGuest?.id === guest.id ? styles.resultCardSelected : styles.resultCard}
              onClick={() => handleSelectGuest(guest)}
            >
              <div style={{ fontWeight: 600, color: TEXT_DARK }}>{guest.fullName}</div>
              <div style={{ fontSize: '0.8rem', color: TEXT_MUTED }}>
                {guest.ticketNumber || '—'}{guest.chapterAffiliation ? ` • ${guest.chapterAffiliation}` : ''}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* QR Preview */}
      {selectedGuest && qrDataUrl && (
        <div style={styles.previewSection}>
          <img src={qrDataUrl} alt="QR Code" style={styles.qrImage} />
          <div style={styles.guestNamePreview}>{selectedGuest.fullName}</div>
          <div style={styles.ticketPreview}>{selectedGuest.ticketNumber}{selectedGuest.registration?.ticket?.tierName ? ` • ${selectedGuest.registration.ticket.tierName}` : ''}</div>
          <div style={styles.detailRow}>
            {selectedGuest.seat?.[0] && `🪑 ${selectedGuest.seat[0].table.tableName || `Table ${selectedGuest.seat[0].table.tableNumber}`}, Seat ${selectedGuest.seat[0].seatNumber}`}
          </div>
          <div style={styles.detailRow}>
            {selectedGuest.chapterAffiliation && `📍 ${selectedGuest.chapterAffiliation}`}
          </div>
          <button style={styles.printBtn} onClick={handlePrintPdf}>🖨️ Print Guest Card (PDF)</button>
        </div>
      )}

      {selectedGuest && !selectedGuest.ticketNumber && (
        <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '8px', fontSize: '0.85rem', color: '#92400e' }}>
          ⚠️ This guest has no ticket number assigned. A QR code cannot be generated without a ticket number.
        </div>
      )}
    </div>
  );
}
